import { graphql, navigate, useStaticQuery } from 'gatsby';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-use';

import musicImg from 'assets/images/podcast/music.svg';
import spotifyImg from 'assets/images/podcast/spotify.svg';
import xmlidImg from 'assets/images/podcast/xmlid.svg';
import youtubeImg from 'assets/images/podcast/youtube.svg';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';

import Pagination from 'molecules/Pagination/NumberedPagination';

import AushaPlayer from 'components/AushaPlayer/AushaPlayer';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import PodcastListingCard from 'components/Cards/PodcastListingCard';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentSingleInstance, ContentfulTemplatePodcast } from 'graphqlTypes';

type TipsToolListingProps = ContentfulComponentSingleInstance & {
  backgroundIsDark?: boolean;
};

const PodcastListing: FC<TipsToolListingProps> = ({ heading, body }) => {
  const { tipsTools } = useStaticQuery(graphql`
    query getAllRecentPodcastQuery {
      tipsTools: allContentfulTemplatePodcast(sort: { fields: publishDate, order: DESC }) {
        nodes {
          ...contentfulTemplatePodcast
        }
      }
    }
  `);

  const posts: ContentfulTemplatePodcast[] = tipsTools && tipsTools?.nodes;

  const seasons: string[] = [];

  posts &&
    // eslint-disable-next-line array-callback-return
    posts.map(post => {
      if (post?.season && !seasons.includes(post.season)) {
        seasons.push(post.season);
      }
    });

  seasons.sort();

  const { pathname } = useLocation();

  const [filtered, setFiltered] = useState<ContentfulTemplatePodcast[]>(posts);
  const [page, setPage] = useState<number>(1);
  const [active] = useState(0);

  const perCount = 12;

  const urlParams = pathname?.split('/') || [];
  const currentPage = pathname?.includes('/page/') ? urlParams[urlParams.length - 1] : 1;
  const baseUrl = '/resources/podcast';

  const goPage = (pg: number) => {
    navigate(`${baseUrl}/${pg === 1 ? '' : `page/${pg}`}`);
  };

  const chunk = (arr: Array<ContentfulTemplatePodcast>) => {
    const chunkSize = 3;
    const chunkedArray = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunkedArray.push(arr.slice(i, i + chunkSize));
    }

    return chunkedArray;
  };

  useEffect(() => {
    setFiltered(active === 0 ? posts : posts?.filter(item => item?.season === seasons[active - 1]));
  }, [active]);

  useEffect(() => {
    setPage(Number(currentPage));
  }, [currentPage]);

  const breadcrumbs = [
    { label: 'Resources', link: '/resources' },
    { label: 'Podcast', link: '/resources/podcast' },
  ];

  const HostedPlatforms = (
    <Flex
      width="100%"
      alignItems="center"
      justifyContent="space-evenly"
      gap="24px"
      marginX="auto"
      marginY={0}
      sm={{ gap: '30px', width: 'fit-content' }}
    >
      <Link href="https://open.spotify.com/show/0xgfGTzqcCflFqwa3e1F0T?si=kCPr_swlScC6k7M1QDE79Q&nd=1">
        <Image
          image={{ file: { url: spotifyImg } }}
          alt="Podcast Music Icon"
          width="42px"
          height="42px"
          lg={{ width: '52px', height: '52px' }}
        />
      </Link>
      <Link href="https://www.youtube.com/playlist?list=PL06vzBGwqWSS0_NQdVrkyVu8SZkjwJlOJ">
        <Image
          image={{ file: { url: youtubeImg } }}
          alt="Podcast Music Icon"
          width="42px"
          height="42px"
          lg={{ width: '52px', height: '52px' }}
        />
      </Link>
      <Link href="https://podcasts.apple.com/us/podcast/field-famous/id1672665453">
        <Image
          image={{ file: { url: xmlidImg } }}
          alt="Podcast Music Icon"
          width="42px"
          height="42px"
          lg={{ width: '52px', height: '52px' }}
        />
      </Link>
      <Link href="https://music.amazon.com/podcasts/8349710b-8b85-478e-a32d-f00df75d068e/field-famous">
        <Image
          image={{ file: { url: musicImg } }}
          alt="Podcast Music Icon"
          width="42px"
          height="42px"
          lg={{ width: '52px', height: '52px' }}
        />
      </Link>
    </Flex>
  );

  return (
    <Flex
      flexDirection="column"
      css={`
        .podcast-player {
          margin-left: -10px;
        }
      `}
    >
      <Flex flexDirection="column">
        <Flex
          position="relative"
          backgroundColor="navy-900"
          flexDirection="column"
          width="100%"
          marginX="auto"
          paddingX={0}
          paddingTop={0}
          gap="80px"
          zIndex="1"
        >
          <Breadcrumbs breadcrumbs={breadcrumbs} isDark={true} />
          <Flex
            flexDirection="column"
            gap="16px"
            width="100%"
            marginX="auto"
            marginY={0}
            lg={{ maxWidth: '90%' }}
            xl={{ maxWidth: '80%' }}
            alignItems="center"
          >
            {heading && (
              <Heading
                fontSize="h3"
                lineHeight="h3"
                textColor="common-white"
                fontFamily="secondaryFont"
                sm={{ fontSize: 'h1', lineHeight: 'h1' }}
              >
                {heading}
              </Heading>
            )}
            {body && (
              <Flex textColor="common-white" fontSize="textMd" sm={{ fontSize: 'textMd' }} lg={{ fontSize: 'textLg' }}>
                {richTextParser(body)}
              </Flex>
            )}
            {HostedPlatforms}
          </Flex>
        </Flex>
        <Flex
          width="100vw"
          height="100%"
          position="absolute"
          backgroundColor="navy-900"
          top="0"
          left="0"
          zIndex="-1"
          maxHeight="500px"
          lg={{ maxHeight: '430px' }}
          xl={{ maxHeight: '470px' }}
        />
      </Flex>
      <Flex
        flexDirection="column"
        paddingBottom={20}
        md={{ marginTop: 32 }}
        lg={{ marginTop: 32, flexDirection: 'row' }}
        xl={{ marginTop: 64 }}
      >
        <Flex
          paddingTop={20}
          paddingBottom={20}
          md={{ paddingTop: 0 }}
          lg={{ paddingBottom: 0, paddingRight: 40, flex: 1 }}
        >
          <Link width="100%" href={`/resources/podcast/${filtered[0].slug}`}>
            <Image
              image={filtered[0].featuredImage as ImageData}
              borderRadius="12px"
              alt={filtered[0].featuredImage.title}
              objectFit="cover"
              objectPosition="left"
              minWidth="100%"
              maxWidth="100%"
              md={{ width: '450px' }}
              lg={{ width: '510px' }}
            />
          </Link>
        </Flex>
        <Flex flexDirection="column" marginAll="auto" lg={{ flex: 1 }}>
          {filtered[0].duration && (
            <Heading textColor="navy-300" as="h4" textStyle="h4">
              {Math.floor(filtered[0].duration / 60)} Minutes
            </Heading>
          )}
          <Heading textColor="common-black" textStyle="h3">
            {filtered[0].title}
          </Heading>
        </Flex>
      </Flex>
      {filtered?.[0]?.aushaEpisodeId && <AushaPlayer episodeId={filtered?.[0]?.aushaEpisodeId} />}
      <Container id="listing-section" backgroundColor="common-transparent" paddingTop={48}>
        {filtered &&
          chunk(filtered.slice((page - 1) * perCount, page * perCount)).map((newPosts, index) => (
            <Flex
              flexWrap="wrap"
              key={index}
              css={`
                .gatsby-image-wrapper {
                  min-width: 100% !important;
                  max-width: 100% !important;
                  min-height: 0 !important;
                  max-height: 100% !important;
                  height: 100%;
                }
                @media (min-width: 991px) {
                  margin: 0 -0.625rem;
                }
              `}
            >
              {newPosts.map((post: ContentfulTemplatePodcast) => (
                <Flex
                  paddingAll={10}
                  key={`item ${post.id}`}
                  width="100%"
                  flex="1 1 auto"
                  sm={{ width: '50%' }}
                  lg={{ flex: 'none', width: '33%' }}
                  css={`
                    a {
                      width: 100%;
                    }
                  `}
                >
                  <PodcastListingCard {...post} backgroundIsDark={false} />
                </Flex>
              ))}
            </Flex>
          ))}

        {filtered?.length > 12 && (
          <Pagination
            paddingTop={20}
            prefix={baseUrl}
            currentPage={page}
            totalPages={Math.ceil(filtered.length / perCount)}
            setCurrentPage={(pg: number) => goPage(pg)}
          />
        )}
      </Container>
    </Flex>
  );
};

export default PodcastListing;
